import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ArrowUpRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_LABELS = {
  mariage: 'Mariage',
  famille: 'Famille',
  sejour: 'Sejour',
  seminaire: 'Seminaire',
  anniversaire: 'Anniversaire',
  google: 'Google',
};

const easeLuxe = [0.16, 1, 0.3, 1];

function EmbedSocialWidget({ widgetId }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!widgetId) return;
    if (!document.getElementById('EmbedSocialHashtagScript')) {
      const script = document.createElement('script');
      script.id = 'EmbedSocialHashtagScript';
      script.src = 'https://embedsocial.com/cdn/ht.js';
      document.head.appendChild(script);
    } else if (window.EmbedSocialHashtagWidget) {
      window.EmbedSocialHashtagWidget.init();
    }
  }, [widgetId]);

  if (!widgetId) return null;
  return (
    <div ref={containerRef} className="mt-12">
      <div className="embedsocial-hashtag" data-ref={widgetId}></div>
    </div>
  );
}

function getInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Reviews() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [widgetId, setWidgetId] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reviews?limit=6`)
      .then(r => setReviews(r.data || []))
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/public/embedsocial-id`)
      .then(r => setWidgetId(r.data?.widget_id || ''))
      .catch(() => {});
  }, []);

  const displayed = reviews.slice(0, 3);
  const ratings = reviews.filter(r => r.rating).map(r => r.rating);
  const avg = ratings.length
    ? (ratings.reduce((a, b) => a + b, 0) / ratings.length)
    : 0;

  return (
    <section
      id="avis"
      data-testid="reviews-section"
      className="py-28 md:py-40 bg-[#1A1C19] relative overflow-hidden"
    >
      {/* Big decorative quote in background */}
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute -top-10 right-4 md:right-12 font-['Cormorant_Garamond'] text-[280px] md:text-[420px] leading-none text-[#CBA77A]/[0.07] font-light"
      >
        &ldquo;
      </span>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
          className="mb-16 md:mb-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-end"
        >
          <motion.div
            variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: easeLuxe } } }}
            className="md:col-span-8 max-w-3xl"
          >
            <span className="overline">Temoignages</span>
            <h2
              data-testid="reviews-title"
              className="font-['Cormorant_Garamond'] text-5xl md:text-7xl font-light text-white mt-5 tracking-[-0.025em] leading-[0.95]"
            >
              Ce qu&apos;ils
              <span className="italic text-white/70"> disent</span>
              <span className="block">de nous</span>
            </h2>
          </motion.div>

          {/* Average rating panel */}
          {avg > 0 && (
            <motion.div
              variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: easeLuxe } } }}
              className="md:col-span-4 md:justify-self-end"
              data-testid="reviews-avg-panel"
            >
              <div className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-2xl p-6 md:p-8 inline-block min-w-[220px]">
                <div className="flex items-baseline gap-2">
                  <span className="font-['Cormorant_Garamond'] text-6xl md:text-7xl font-light text-[#CBA77A] tracking-[-0.03em] leading-none">
                    {avg.toFixed(1).replace('.', ',')}
                  </span>
                  <span className="text-white/45 text-lg font-light">/5</span>
                </div>
                <div className="flex gap-0.5 mt-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star
                      key={j}
                      className={`w-3.5 h-3.5 ${j < Math.round(avg) ? 'fill-[#CBA77A] text-[#CBA77A]' : 'text-white/15'}`}
                      strokeWidth={1.5}
                    />
                  ))}
                </div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/50 mt-3 font-medium">
                  Note moyenne &middot; {reviews.length} avis verifies
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Reviews grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7">
          {displayed.map((review, i) => {
            const name = review.author_name || review.author || review.name || 'Anonyme';
            const eventType = review.event_type || review.source || '';
            const dateStr = review.date || '';
            const formattedDate = dateStr
              ? new Date(dateStr).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })
              : '';
            const occasionLine = [EVENT_LABELS[eventType] || eventType, formattedDate]
              .filter(Boolean)
              .join(' \u00B7 ');

            return (
              <motion.article
                key={review.id || i}
                data-testid={`homepage-review-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: easeLuxe, delay: i * 0.08 }}
                className="group relative bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-[1.25rem] md:rounded-[1.5rem] p-8 md:p-9 hover:bg-white/[0.06] hover:border-[#CBA77A]/40 hover:-translate-y-1 hover:shadow-[0_24px_60px_-16px_rgba(203,167,122,0.18)] transition-all duration-500 flex flex-col"
              >
                {/* Rating */}
                <div className="flex gap-0.5 mb-6">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star
                      key={j}
                      className={`w-3.5 h-3.5 ${j < review.rating ? 'fill-[#CBA77A] text-[#CBA77A]' : 'text-white/15'}`}
                      strokeWidth={1.5}
                    />
                  ))}
                </div>

                {/* Citation */}
                <p className="font-['Cormorant_Garamond'] text-white text-xl md:text-2xl leading-[1.4] font-light italic mb-8 flex-1">
                  &ldquo;{review.text}&rdquo;
                </p>

                {/* Author */}
                <div className="border-t border-white/10 pt-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#CBA77A]/15 border border-[#CBA77A]/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#CBA77A] text-[11px] font-semibold tracking-[0.1em]">
                      {getInitials(name)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-[11px] font-semibold tracking-[0.22em] uppercase truncate">
                      {name}
                    </p>
                    {occasionLine && (
                      <p className="text-white/50 text-[10px] mt-0.5 font-light tracking-wide truncate">
                        {occasionLine}
                      </p>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* EmbedSocial Google Reviews Widget */}
        {widgetId && (
          <div data-testid="google-reviews-widget">
            <div className="text-center mt-16 mb-6">
              <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/10 rounded-full px-4 py-2 text-xs text-white/70 backdrop-blur-md">
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="tracking-wide">Avis Google verifies</span>
              </div>
            </div>
            <EmbedSocialWidget widgetId={widgetId} />
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-16">
          <button
            type="button"
            data-testid="voir-tous-avis-btn"
            onClick={() => navigate('/avis')}
            className="group inline-flex items-center gap-2 text-white hover:text-[#CBA77A] text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors duration-300 pb-1 border-b border-white/30 hover:border-[#CBA77A]"
          >
            <span>Voir tous les temoignages</span>
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </section>
  );
}
